import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/Button';
import { useAuthStore } from '../../store/useAuthStore';
import { useDossier } from '../../hooks/queries/useDossiers';
import {
  useCreateDossierHearing,
  useDeleteDossierHearing,
  useHearingAttendees,
  useUpdateDossierHearing,
} from '../../hooks/queries/useDossierHearings';
import type { DossierHearing, DossierHearingAttendee } from '../../services/dossiers/hearingService';
import {
  applyCourtPrefill,
  commitHearingDraft,
  emptyHearingDraft,
  hearingDraftFromRecord,
  type HearingDraft,
  type HearingPerson,
} from '../../services/dossiers/hearingForm';
import { HearingFields, useHearingPeopleOptions } from './HearingFields';

const SAVE_ERROR = {
  label: 'Le libellé est obligatoire',
  day: 'Indiquez la date de début',
  end: 'L’heure de fin doit être après le début',
} as const;

function peopleFromAttendees(rows: DossierHearingAttendee[]): HearingPerson[] {
  const people: HearingPerson[] = [];
  for (const row of rows) {
    if (row.collaborator_id) people.push({ kind: 'collaborator', id: row.collaborator_id });
    else if (row.contact_id) people.push({ kind: 'contact', id: row.contact_id });
  }
  return people;
}

interface Props {
  open: boolean;
  dossierId: string;
  hearing: DossierHearing | null;
  onClose: () => void;
}

export function HearingModal({ open, dossierId, hearing, onClose }: Props) {
  const create = useCreateDossierHearing(dossierId);
  const update = useUpdateDossierHearing(dossierId);
  const remove = useDeleteDossierHearing(dossierId);
  const meId = useAuthStore((s) => s.collaborator?.id ?? null);
  const editing = hearing !== null;
  const attendees = useHearingAttendees(open && hearing ? hearing.id : undefined);
  const dossier = useDossier(open ? dossierId : undefined);

  const [draft, setDraft] = useState<HearingDraft>(() => emptyHearingDraft(meId));
  const loadedHearingId = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      loadedHearingId.current = null;
      return;
    }
    if (hearing) return;
    setDraft(emptyHearingDraft(meId));
  }, [open, hearing, meId]);

  useEffect(() => {
    if (!open || hearing) return;
    setDraft((current) => applyCourtPrefill(current, dossier.data?.juridiction ?? null, 'create'));
  }, [open, hearing, dossier.data?.juridiction]);

  useEffect(() => {
    if (!open || !hearing || attendees.isLoading || !attendees.data) return;
    if (loadedHearingId.current === hearing.id) return;
    loadedHearingId.current = hearing.id;
    setDraft(hearingDraftFromRecord(hearing, peopleFromAttendees(attendees.data)));
  }, [open, hearing, attendees.data, attendees.isLoading]);

  const keepIds = useMemo(() => {
    const ids = draft.people
      .filter((person) => person.kind === 'collaborator')
      .map((person) => person.id);
    if (draft.ownerId) ids.push(draft.ownerId);
    return ids;
  }, [draft.ownerId, draft.people]);

  const options = useHearingPeopleOptions(open ? dossierId : undefined, keepIds);
  const pending = create.isPending || update.isPending || remove.isPending;
  const ready = !editing || (attendees.isSuccess && !attendees.isLoading);
  const committed = commitHearingDraft(draft, {
    collaboratorIds: options.collaborators.map((person) => person.id),
    contactIds: options.parties.map((person) => person.id),
  });

  function save() {
    if (!committed.ok) {
      toast.error(SAVE_ERROR[committed.reason]);
      return;
    }
    const input = { ...committed.payload, people: committed.people };
    const done = {
      onSuccess: () => {
        toast.success('Audience enregistrée');
        onClose();
      },
      onError: (err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Impossible d’enregistrer');
      },
    };
    if (editing && hearing) {
      update.mutate({ id: hearing.id, patch: input }, done);
      return;
    }
    create.mutate(input, done);
  }

  function onDelete() {
    if (!hearing) return;
    remove.mutate(hearing.id, {
      onSuccess: () => {
        toast.success('Audience supprimée');
        onClose();
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Impossible de supprimer');
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-[920px] gap-0 overflow-y-auto border-0 bg-[#fffdf8] p-0 shadow-[0_24px_60px_rgba(2,20,16,0.28)] sm:rounded-2xl">
        {editing && attendees.isLoading ? (
          <p className="px-6 py-8 text-sm text-cime-text-muted">Chargement de l’audience…</p>
        ) : editing && attendees.isError ? (
          <p className="px-6 py-8 text-sm text-cime-urgent">Impossible de charger les personnes concernées.</p>
        ) : (
          <HearingFields
            draft={draft}
            onChange={setDraft}
            disabled={pending || !ready}
            collaborators={options.collaborators}
            parties={options.parties}
            context={dossier.data ? `${dossier.data.number} · ${dossier.data.title}` : undefined}
            title={(
              <DialogTitle className="font-body text-[11px] font-bold uppercase tracking-[0.14em] text-cime-gold-text">
                {editing ? 'Modifier l’audience' : 'Nouvelle audience'}
              </DialogTitle>
            )}
          />
        )}
        <div className="flex items-center justify-between gap-2 border-t border-[#e6e2d8] bg-white px-[22px] py-3.5">
          {editing ? (
            <Button type="button" variant="danger" size="sm" onClick={onDelete} disabled={pending}>
              Supprimer
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={pending}
              className="border border-[#e6e2d8] bg-white font-bold text-cime-text-primary hover:bg-[#f6f4ee]"
            >
              Annuler
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={save}
              disabled={!ready || !committed.ok || pending}
              isLoading={create.isPending || update.isPending}
              className="font-bold"
            >
              {editing ? 'Enregistrer' : 'Enregistrer l’audience'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
